import type { RuleModule } from '../engine/registry';
import { OK, reject, type GameRuntime, type GameState } from '../engine/state';
import { bagIsCanonical, bagTotal, type ResourceBag } from '../engine/gameDef';
import type { SeatIdx } from '../types';

// Maritime trade (outline §8): 4:1 always; 3:1 with an own building on a generic-harbor
// vertex; 2:1 for THAT resource with an own building on its specific harbor — no break on
// anything else. Rates are OPTIONS: any AVAILABLE rate is legal (deliberate Supply
// returns under the shortage economy — D3); harbors work the turn they're settled
// (outline §14.21) because rates derive live from occupancy. Active-window only; the
// paired partner may supply-trade (outline §13.2).

/**
 * The set of legal give-counts for `resource`, from the vertex occupancy alone.
 *
 * Extracted from `availableRates` so the CLIENT can show a seat its own rates (H7-S1,
 * T-UI-026) without reconstructing a GameState it is never given: `GameView.board` is
 * structurally `GameState['board']`, so a redacted view feeds this directly. One rule,
 * one implementation — the panel must never re-derive §8.2 for itself.
 */
export function ratesFromVertices(vertices: GameState['board']['vertices'], env: GameRuntime, seat: SeatIdx, resource: string): Set<number> {
  const rates = new Set<number>([env.cfg.engine.harborRates.base]);
  for (const [vid, placement] of Object.entries(vertices)) {
    if (placement.owner !== seat) continue;
    const harbor = env.board.vertices.get(vid)?.harbor ?? null;
    if (harbor === null) continue;
    if (harbor === '3:1') rates.add(env.cfg.engine.harborRates.generic);
    else if (harbor === resource) rates.add(env.cfg.engine.harborRates.specific); // its resource ONLY (outline §8)
  }
  return rates;
}

/** The set of legal give-counts for `resource` (plan §8.2 availableRates). */
export function availableRates(state: GameState, env: GameRuntime, seat: SeatIdx, resource: string): Set<number> {
  return ratesFromVertices(state.board.vertices, env, seat, resource);
}

function inTradeWindow(state: GameState, seat: SeatIdx): boolean {
  const p = state.phase;
  return (p.t === 'ACTION' || p.t === 'PARTNER_ACTION') && p.seat === seat; // SBP never trades (outline §13.2)
}

export const supplyTradeModule: RuleModule = {
  id: 'supply-trade',
  register(ctx) {
    ctx.action('SUPPLY_TRADE', {
      enumerate(state, env, seat) {
        if (!inTradeWindow(state, seat)) return null;
        const out: Array<Record<string, unknown>> = [];
        for (const giveRes of env.def.resources) {
          const held = state.seats[seat]!.hand[giveRes.id] ?? 0;
          for (const rate of availableRates(state, env, seat, giveRes.id)) {
            if (held < rate) continue;
            for (const getRes of env.def.resources) {
              if (getRes.id === giveRes.id) continue;
              if ((state.supply[getRes.id] ?? 0) < 1) continue;
              out.push({ give: { [giveRes.id]: rate }, get: { [getRes.id]: 1 } });
            }
          }
        }
        return out;
      },
      validate(state, action, env) {
        if (!inTradeWindow(state, action.seat)) return reject('WRONG_PHASE');
        const give = action.payload['give'];
        const get = action.payload['get'];
        if (typeof give !== 'object' || give === null || Array.isArray(give)) return reject('BAD_PAYLOAD');
        if (typeof get !== 'object' || get === null || Array.isArray(get)) return reject('BAD_PAYLOAD');
        const giveBag = give as ResourceBag;
        const getBag = get as ResourceBag;
        if (!bagIsCanonical(giveBag) || !bagIsCanonical(getBag)) return reject('BAD_PAYLOAD');
        const giveKeys = Object.keys(giveBag);
        const getKeys = Object.keys(getBag);
        if (giveKeys.length !== 1 || getKeys.length !== 1) return reject('ONE_TYPE_EACH_SIDE');
        const giveRes = giveKeys[0]!;
        const getRes = getKeys[0]!;
        if (!env.def.resources.some((r) => r.id === giveRes) || !env.def.resources.some((r) => r.id === getRes)) return reject('BAD_PAYLOAD');
        if (giveRes === getRes) return reject('LIKE_FOR_LIKE');
        if (bagTotal(getBag) !== 1) return reject('GET_ONE_CARD');
        const k = giveBag[giveRes]!;
        if (!availableRates(state, env, action.seat, giveRes).has(k)) return reject('NO_SUCH_RATE');
        if ((state.seats[action.seat]!.hand[giveRes] ?? 0) < k) return reject('CARDS_NOT_HELD');
        if ((state.supply[getRes] ?? 0) < 1) return reject('SUPPLY_SHORT');
        return OK;
      },
      apply(state, action, _env, emit) {
        const give = action.payload['give'] as ResourceBag;
        const get = action.payload['get'] as ResourceBag;
        const giveRes = Object.keys(give)[0]!;
        const getRes = Object.keys(get)[0]!;
        const k = give[giveRes]!;
        state.seats[action.seat]!.hand[giveRes] = (state.seats[action.seat]!.hand[giveRes] ?? 0) - k;
        if (state.seats[action.seat]!.hand[giveRes] === 0) delete state.seats[action.seat]!.hand[giveRes];
        state.supply[giveRes] = (state.supply[giveRes] ?? 0) + k;
        state.supply[getRes] = (state.supply[getRes] ?? 0) - 1;
        state.seats[action.seat]!.hand[getRes] = (state.seats[action.seat]!.hand[getRes] ?? 0) + 1;
        emit({ kind: 'SUPPLY_TRADED', vis: 'public', seat: action.seat, give: { ...give }, get: { ...get } });
      },
    });
  },
};
